import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2, ShieldCheck } from "lucide-react";
import { getProduct } from "@/data/products";
import Seo from "@/components/site/Seo";
import ComingSoon from "@/components/site/ComingSoon";
import { EASE, Eyebrow } from "@/components/site/motion";

const API = process.env.REACT_APP_BACKEND_URL;

const inputClass =
  "h-[52px] w-full rounded-[10px] border border-slate-200 bg-white px-4 text-[15px] text-slate-900 outline-none transition-[border-color,box-shadow] duration-200 placeholder:text-slate-400 focus:border-blue-600 focus:ring-4 focus:ring-blue-600/10";
const labelClass = "mb-2 block text-[13px] font-medium text-slate-700";

function Field({ label, required, error, children, testId }) {
  return (
    <div data-testid={testId}>
      <label className={labelClass}>
        {label} {required && <span className="text-blue-600">*</span>}
      </label>
      {children}
      {error && <p className="mt-1.5 text-xs text-red-600">{error}</p>}
    </div>
  );
}

const initialForm = {
  name: "",
  email: "",
  phone: "",
  company: "",
  address: "",
  city: "",
  state: "",
  pincode: "",
  country: "India",
  gstin: "",
  notes: "",
  consent: false,
};

export default function CheckoutPage() {
  const { slug } = useParams();
  const p = getProduct(slug);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [orderId, setOrderId] = useState("");
  const [serverError, setServerError] = useState("");

  if (!p) return <ComingSoon />;

  const set = (key) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (!form.name.trim()) er.name = "Please enter your full name.";
    if (!form.email.trim()) er.email = "Please enter your email.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) er.email = "Please enter a valid email address.";
    if (!form.phone.trim()) er.phone = "Please enter your phone number.";
    if (!form.address.trim()) er.address = "Please enter your billing address.";
    if (!form.city.trim()) er.city = "Please enter your city.";
    if (!form.state.trim()) er.state = "Please enter your state.";
    if (!form.pincode.trim()) er.pincode = "Please enter your PIN / postal code.";
    if (!form.consent) er.consent = "Please agree to the policies to continue.";
    return er;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const er = validate();
    if (Object.keys(er).length > 0) {
      setErrors(er);
      return;
    }
    setStatus("sending");
    setServerError("");
    try {
      const res = await fetch(`${API}/api/orders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ product_slug: p.slug, ...form }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Something went wrong while placing your order.");
      }
      const data = await res.json();
      setOrderId(data.order_id);
      setStatus("success");
      window.scrollTo({ top: 0, behavior: "auto" });
    } catch (err) {
      setServerError(err.message || "Something went wrong. Please try again or email hello@bytevora.com.");
      setStatus("idle");
    }
  };

  return (
    <main className="bg-[#F8FAFC] pt-[72px]" data-testid="checkout-page">
      <Seo title={`Checkout — ${p.name}`} description={`Complete your order for ${p.name} (${p.priceLabel}) from Bytevora.`} path={`/checkout/${p.slug}`} />

      {status === "success" ? (
        <div className="mx-auto flex min-h-[70vh] w-full max-w-[680px] items-center px-6 py-20">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="w-full rounded-[24px] border border-slate-200 bg-white p-10 text-center shadow-[0_30px_60px_-45px_rgba(11,18,32,0.3)]"
            data-testid="checkout-success"
          >
            <span className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-blue-600/10 text-blue-600">
              <CheckCircle2 size={30} strokeWidth={1.8} />
            </span>
            <h1 className="mt-6 text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">
              Your order has been received.
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-slate-600 md:text-base">
              Thank you, {form.name.split(" ")[0]}. We&rsquo;ve emailed your order confirmation and
              next steps to <span className="font-medium text-slate-900">{form.email}</span>.
            </p>
            <div className="mt-7 inline-flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-5 py-3">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">Order reference</span>
              <span className="font-mono text-sm font-semibold tracking-wide text-blue-600" data-testid="checkout-order-id">{orderId}</span>
            </div>
            <div className="mt-8 rounded-xl border border-slate-100 bg-slate-50/60 p-5 text-left">
              <p className="text-[13px] font-semibold text-slate-900">What happens next</p>
              <ol className="mt-3 list-decimal space-y-1.5 pl-5 text-[13px] leading-relaxed text-slate-600">
                <li>We review your order details.</li>
                <li>We email payment instructions and anything we need from you to begin.</li>
                <li>The delivery timeline begins once payment and required information are received.</li>
              </ol>
            </div>
            <Link
              to="/products"
              data-testid="checkout-back-to-products"
              className="group mt-8 inline-flex items-center gap-2 text-sm font-semibold text-slate-900 transition-colors duration-300 hover:text-blue-600"
            >
              <ArrowLeft size={15} className="transition-transform duration-300 group-hover:-translate-x-1" />
              Back to Products
            </Link>
          </motion.div>
        </div>
      ) : (
        <div className="mx-auto w-full max-w-[1200px] px-6 py-14 md:px-8 md:py-20">
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
            <Link
              to={`/products/${p.slug}`}
              data-testid="checkout-back-link"
              className="group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500 transition-colors hover:text-blue-600"
            >
              <ArrowLeft size={13} className="transition-transform duration-300 group-hover:-translate-x-0.5" />
              Back to {p.name}
            </Link>
            <div className="mt-6">
              <Eyebrow>Checkout</Eyebrow>
              <h1 data-testid="checkout-heading" className="mt-4 text-3xl font-extrabold tracking-[-0.02em] text-slate-900 md:text-5xl">
                Complete your order.
              </h1>
              <p className="mt-3 max-w-xl text-sm leading-relaxed text-slate-600 md:text-base">
                Submit your details and we&rsquo;ll email your order confirmation, payment
                instructions, and next steps.
              </p>
            </div>
          </motion.div>

          <div className="mt-12 grid items-start gap-10 lg:grid-cols-[1.25fr_0.75fr]">
            <motion.form
              onSubmit={onSubmit}
              noValidate
              data-testid="checkout-form"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE, delay: 0.12 }}
              className="rounded-[20px] border border-slate-200 bg-white p-7 md:p-9"
            >
              <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-slate-400">01 — Contact details</p>
              <div className="mt-6 grid gap-5 sm:grid-cols-2">
                <Field label="Full Name" required error={errors.name} testId="co-field-name">
                  <input type="text" value={form.name} onChange={set("name")} placeholder="Your name" data-testid="co-input-name" className={`${inputClass} ${errors.name ? "border-red-400" : ""}`} />
                </Field>
                <Field label="Email" required error={errors.email} testId="co-field-email">
                  <input type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" data-testid="co-input-email" className={`${inputClass} ${errors.email ? "border-red-400" : ""}`} />
                </Field>
                <Field label="Phone" required error={errors.phone} testId="co-field-phone">
                  <input type="tel" value={form.phone} onChange={set("phone")} placeholder="+91 ..." data-testid="co-input-phone" className={`${inputClass} ${errors.phone ? "border-red-400" : ""}`} />
                </Field>
                <Field label="Company / Organization" testId="co-field-company">
                  <input type="text" value={form.company} onChange={set("company")} placeholder="Company name" data-testid="co-input-company" className={inputClass} />
                </Field>
              </div>

              <p className="mt-10 font-mono text-[11px] uppercase tracking-[0.24em] text-slate-400">02 — Billing details</p>
              <div className="mt-6 grid gap-5 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <Field label="Billing Address" required error={errors.address} testId="co-field-address">
                    <input type="text" value={form.address} onChange={set("address")} placeholder="Street address" data-testid="co-input-address" className={`${inputClass} ${errors.address ? "border-red-400" : ""}`} />
                  </Field>
                </div>
                <Field label="City" required error={errors.city} testId="co-field-city">
                  <input type="text" value={form.city} onChange={set("city")} placeholder="City" data-testid="co-input-city" className={`${inputClass} ${errors.city ? "border-red-400" : ""}`} />
                </Field>
                <Field label="State" required error={errors.state} testId="co-field-state">
                  <input type="text" value={form.state} onChange={set("state")} placeholder="State" data-testid="co-input-state" className={`${inputClass} ${errors.state ? "border-red-400" : ""}`} />
                </Field>
                <Field label="PIN / Postal Code" required error={errors.pincode} testId="co-field-pincode">
                  <input type="text" value={form.pincode} onChange={set("pincode")} placeholder="226010" data-testid="co-input-pincode" className={`${inputClass} ${errors.pincode ? "border-red-400" : ""}`} />
                </Field>
                <Field label="Country" testId="co-field-country">
                  <input type="text" value={form.country} onChange={set("country")} data-testid="co-input-country" className={inputClass} />
                </Field>
                <div className="sm:col-span-2">
                  <Field label="GSTIN (optional, for GST invoice)" testId="co-field-gstin">
                    <input type="text" value={form.gstin} onChange={set("gstin")} placeholder="22AAAAA0000A1Z5" data-testid="co-input-gstin" className={`${inputClass} font-mono`} />
                  </Field>
                </div>
              </div>

              <p className="mt-10 font-mono text-[11px] uppercase tracking-[0.24em] text-slate-400">03 — Project details</p>
              <div className="mt-6" data-testid="co-field-notes">
                <label className={labelClass}>Anything we should know? (website URL, repository, timeline…)</label>
                <textarea
                  value={form.notes}
                  onChange={set("notes")}
                  placeholder="Share links, context, or requirements relevant to this product."
                  data-testid="co-input-notes"
                  className="min-h-[120px] w-full resize-y rounded-[10px] border border-slate-200 bg-white px-4 py-3.5 text-[15px] text-slate-900 outline-none transition-[border-color,box-shadow] duration-200 placeholder:text-slate-400 focus:border-blue-600 focus:ring-4 focus:ring-blue-600/10"
                />
              </div>

              <div className="mt-7" data-testid="co-field-consent">
                <label className="flex cursor-pointer items-start gap-3">
                  <input
                    type="checkbox"
                    checked={form.consent}
                    onChange={set("consent")}
                    data-testid="co-input-consent"
                    className="mt-0.5 h-[18px] w-[18px] shrink-0 cursor-pointer rounded border-slate-300 accent-blue-600"
                  />
                  <span className="text-[13px] leading-relaxed text-slate-600">
                    I acknowledge the product scope and agree to Bytevora&rsquo;s{" "}
                    <Link to="/terms-and-conditions" className="font-medium text-blue-600">Terms &amp; Conditions</Link>,{" "}
                    <Link to="/refund-cancellation-policy" className="font-medium text-blue-600">Refund &amp; Cancellation Policy</Link>, and{" "}
                    <Link to="/shipping-delivery-policy" className="font-medium text-blue-600">Shipping &amp; Delivery Policy</Link>.
                  </span>
                </label>
                {errors.consent && <p className="mt-1.5 text-xs text-red-600">{errors.consent}</p>}
              </div>

              {serverError && (
                <p className="mt-5 rounded-[10px] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="checkout-error">
                  {serverError}
                </p>
              )}

              <button
                type="submit"
                data-testid="checkout-submit-button"
                disabled={status === "sending"}
                className="group mt-8 inline-flex w-full items-center justify-center gap-2.5 rounded-[10px] bg-navy px-7 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:bg-blue-600 disabled:opacity-70"
              >
                {status === "sending" ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Placing order…
                  </>
                ) : (
                  <>
                    Place Order — {p.priceLabel}
                    <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </>
                )}
              </button>
              <p className="mt-4 flex items-center gap-2 text-[12px] text-slate-400">
                <ShieldCheck size={14} className="text-slate-300" />
                Your details are used only to process this order. See our{" "}
                <Link to="/privacy-policy" className="text-blue-600">Privacy Policy</Link>.
              </p>
            </motion.form>

            <motion.aside
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE, delay: 0.22 }}
              className="rounded-[20px] border border-slate-200 bg-white p-7 shadow-[0_30px_60px_-45px_rgba(11,18,32,0.3)] lg:sticky lg:top-28"
              data-testid="checkout-summary"
            >
              <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-slate-400">Order summary</p>
              <div className="mt-5 flex items-start gap-4">
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl border border-slate-200 bg-slate-50 text-blue-600">
                  <p.icon size={19} strokeWidth={1.7} />
                </span>
                <div>
                  <p className="text-[15px] font-semibold tracking-tight text-slate-900">{p.name}</p>
                  <p className="mt-1 text-xs text-slate-500">{p.category}</p>
                </div>
              </div>
              <div className="mt-6 space-y-3 border-t border-slate-100 pt-5 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Price</span>
                  <span className="font-semibold text-slate-900">{p.priceLabel}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Delivery</span>
                  <span className="text-right font-medium text-slate-700">{p.delivery}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Delivery type</span>
                  <span className="text-right font-medium text-slate-700">{p.deliveryType}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Taxes</span>
                  <span className="text-right text-xs text-slate-400">Communicated before payment, if applicable</span>
                </div>
              </div>
              <div className="mt-5 flex items-center justify-between border-t border-slate-200 pt-5">
                <span className="text-sm font-semibold text-slate-900">Total</span>
                <span className="text-[26px] font-bold tracking-tight text-slate-900" data-testid="checkout-total">{p.priceLabel}</span>
              </div>
              <p className="mt-4 text-[11.5px] leading-relaxed text-slate-400">
                After placing your order, our team emails payment instructions and next steps.
                Work begins once payment and required information are received.
              </p>
            </motion.aside>
          </div>
        </div>
      )}
    </main>
  );
}
